import { QueryClientProvider } from '@tanstack/react-query'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { z } from 'zod'

import { RouteError } from '@/components/route-error'
import { registerServiceWorker, reloadOnChunkFailure } from '@/lib/pwa'
import { queryClient } from '@/lib/query-client'
import { initTheme } from '@/lib/theme'
import { routeTree } from '@/routeTree.gen'

import './index.css'

// zod 4 probes `new Function('')` on the first object parse to decide whether it may compile a
// JIT parser. The page CSP has no 'unsafe-eval', so the probe throws, zod catches it and falls
// back, and the browser still files a script-src violation against our own bundle. `jitless`
// skips the probe. Must run before any schema parses, hence here rather than next to a schema.
z.config({ jitless: true })

initTheme()

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0, // let TanStack Query own staleness
  defaultErrorComponent: RouteError,
  scrollRestoration: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

reloadOnChunkFailure(window, () => window.location.reload())
registerServiceWorker()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
)
